import { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import Colors from '../../ui/global/Colors';
import Button from '../../ui/components/Button';
import Checkbox from '../../ui/components/Checkbox';
import { defaultConfig } from '../../utils/config';
import { useUser } from '../../backend/useUser';
import { useNavigate } from 'react-router-dom';
import { analyzeGameTemplates } from './analyzeTemplates';
import { runCodeMigration, onCancelMigrationClick } from './migrateCode';
import { removeOldTemplates } from './removeOldTemplates';

const Root = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-height: 100svh;
    background-color: ${Colors.black};
    color: ${Colors.white};
    padding: 5px;
    gap: 10px;
    overflow-x: hidden;
    overflow-y: auto;
`;

const OptionsRow = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 20px;
`;

const CheckboxContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    color: ${Colors.white};
    font-size: 16px;
`;

const GameIdInput = styled.input`
    padding: 10px;
    border: 1px solid ${Colors.white};
    border-radius: 5px;
    background-color: ${Colors.black};
    color: ${Colors.white};
    font-size: 16px;
    width: 250px;
    text-align: left;
    ::placeholder {
        color: ${Colors.grey5};
    }
`;

const Separator = styled.div`
    height: 1px;
    width: 100%;
    max-width: 400px;
    background-color: ${Colors.grey8};
    margin: 15px 0;
`;

const Debug = () => {
    const navigate = useNavigate();
    const { user, loading } = useUser();
    const [gameId, setGameId] = useState('');
    const [dryRun, setDryRun] = useState(true);
    const [runAll, setRunAll] = useState(true);
    const [isMigrating, setIsMigrating] = useState(false);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [writeToDB, setWriteToDB] = useState(false);
    const [analysisCurrent, setAnalysisCurrent] = useState(0);
    const [analysisTotal, setAnalysisTotal] = useState<number | null>(null);
    const [isRemoving, setIsRemoving] = useState(false);
    const [removalAction, setRemovalAction] = useState<'hide' | 'delete' | null>(null);
    const [removalCurrent, setRemovalCurrent] = useState(0);
    const [removalTotal, setRemovalTotal] = useState<number | null>(null);
    const [batchCounter, setBatchCounter] = useState(0);
    const [totalRemaining, setTotalRemaining] = useState<number | null>(null);
    const cancelRef = useRef(false);

    useEffect(() => {
        if (!loading) {
            if (!user || !defaultConfig.ADMIN_EMAILS.includes(user.email)) {
                navigate('/');
            }
        }
    }, [user, loading]);
    
    // This function handles the iterative calling
    const onMigrateCodeToStorageClick = async () => {
        await runCodeMigration(
            gameId, 
            dryRun, 
            runAll, 
            cancelRef, 
            setBatchCounter, 
            setTotalRemaining, 
            setIsMigrating
        );
    };

    const onAnalyzeGameTemplatesClick = async () => {
        setIsAnalyzing(true);
        setAnalysisCurrent(0); // Reset progress
        setAnalysisTotal(null); // Reset total
        console.log('analyze game templates clicked - Starting analysis...');
        try {
            const onProgress = (current: number, total: number) => {
                setAnalysisCurrent(current);
                setAnalysisTotal(total);
                console.log(`Analyzing: ${current}/${total}`);
            };

            const result = await analyzeGameTemplates(writeToDB, onProgress);
            console.log('Game Template Analysis Result: Analysis Complete.', result);
        } catch (error) {
            console.error('Error analyzing game templates: Analysis Failed.', error);
        } finally {
            setIsAnalyzing(false);
            setAnalysisCurrent(0);
            setAnalysisTotal(null);
        }
    };
    
    const onHideOldTemplatesClick = async () => {
        setRemovalAction('hide');
        await handleTemplateRemoval('hide');
    };

    const onDeleteOldTemplatesClick = async () => {
        setRemovalAction('delete');
        await handleTemplateRemoval('delete');
    };
    
    const handleTemplateRemoval = async (action: 'hide' | 'delete') => {
        setIsRemoving(true);
        setRemovalCurrent(0);
        setRemovalTotal(null);
        console.log(`remove old templates clicked - Starting ${action} operation...`);
        try {
            const onProgress = (current: number, total: number) => {
                // This progress is for games found to update, based on total count
                setRemovalCurrent(current);
                setRemovalTotal(total);
                console.log(`Template Removal (${action}): ${current} games found to update.`);
            };

            const result = await removeOldTemplates(action, onProgress);
            console.log(`Template Removal Result (${action}): Operation Complete.`, result);
        } catch (error) {
            console.error(`Error ${action}ing old templates: Operation Failed.`, error);
        } finally {
            setIsRemoving(false);
            setRemovalAction(null);
            setRemovalCurrent(0);
            setRemovalTotal(null);
        }
    };

    const isTargetingSingleGame = gameId.trim().length > 0;

    const buttonText = isMigrating 
        ? `Migrating... (Batch ${batchCounter}${totalRemaining !== null ? ` | Remaining: ${totalRemaining}` : ''})` 
        : isTargetingSingleGame
        ? `Migrate Game ID: ${gameId.trim()}`
        : "Migrate Code To Storage";
        
    const analyzeButtonText = isAnalyzing
        ? `Analyzing Entry... (${analysisCurrent}${analysisTotal !== null ? `/${analysisTotal}` : ''})`
        : `Analyze Game Templates${writeToDB ? ' (Writing to DB)' : ''}`;
        
    const getRemovalButtonText = (action: 'hide' | 'delete') => {
        if (!isRemoving || removalAction !== action) {
            return `${action === 'hide' ? 'Hide' : 'Delete'} Old`;
        }
        
        // When actively removing
        return `${action === 'hide' ? 'Hiding' : 'Deleting'}... (${removalCurrent}${removalTotal !== null ? `/${removalTotal}` : ''})`;
    };

    return (
        <Root>
            <GameIdInput
                placeholder="Optional: Game ID"
                value={gameId}
                onChange={(e) => setGameId(e.target.value)}
                disabled={isMigrating}
            />
            <OptionsRow>
                <CheckboxContainer>
                    Run All
                    <Checkbox
                        checked={isTargetingSingleGame ? false : runAll} // Run All is effectively false if a Game ID is present
                        onChange={(e, checked) => setRunAll(checked)}
                        colorMode="dark"
                        size={28.5}
                        disabled={isTargetingSingleGame || isMigrating} // Disable if targeting a single game
                    />
                </CheckboxContainer>
                <CheckboxContainer>
                    Dry Run
                    <Checkbox
                        checked={dryRun}
                        onChange={(e, checked) => setDryRun(checked)}
                        colorMode="dark"
                        size={28.5}
                        disabled={isMigrating}
                    />
                </CheckboxContainer>
            </OptionsRow>
            <Button 
                text={buttonText}
                color="white"
                onClick={onMigrateCodeToStorageClick}
                disabled={isMigrating && (isTargetingSingleGame ? false : runAll)} // Disable button only while actively running a batch loop OR if single game migration is happening
            />
            {isMigrating && !isTargetingSingleGame && runAll && ( // Only show Cancel if running all, and NOT targeting a single game
                <Button 
                    text="Cancel"
                    color="red"
                    onClick={() => onCancelMigrationClick(cancelRef)}
                />
            )}

            <Separator />

            <OptionsRow>
                <CheckboxContainer>
                    Write Template to Database
                    <Checkbox
                        checked={writeToDB}
                        onChange={(e, checked) => setWriteToDB(checked)}
                        colorMode="dark"
                        size={28.5}
                        disabled={isAnalyzing}
                    />
                </CheckboxContainer>
            </OptionsRow>
            <Button
                size="small"
                color="white"
                text={analyzeButtonText}
                onClick={onAnalyzeGameTemplatesClick}
                disabled={isAnalyzing}
            />
            <OptionsRow>
                <Button
                    size="small"
                    color="black"
                    text={getRemovalButtonText('hide')}
                    onClick={onHideOldTemplatesClick}
                    disabled={isRemoving && removalAction !== 'hide'}
                />
                <Button
                    size="small"
                    color="black"
                    text={getRemovalButtonText('delete')}
                    onClick={onDeleteOldTemplatesClick}
                    disabled={isRemoving && removalAction !== 'delete'}
                />
            </OptionsRow>
        </Root>
    )
};

export default Debug;